import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Users, ShieldCheck, ShieldAlert, UserX,
  Filter, ArrowUpDown, FileSpreadsheet,
  ChevronUp, ChevronDown,
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import DateFilter from "../components/DateFilter";
import { SyncDataButton } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  Trusted:   { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", dot: "bg-emerald-400" },
  Monitor:   { bg: "bg-amber-500/10",   text: "text-amber-400",   border: "border-amber-500/20",   dot: "bg-amber-400" },
  "High Risk": { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20", dot: "bg-red-400" },
  Inactive:  { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/20", dot: "bg-slate-500" },
};

export default function PublisherReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [publisherFilter, setPublisherFilter] = useState("");
  const [publisherNames, setPublisherNames] = useState([]);
  const [sortCol, setSortCol] = useState("total_calls");
  const [sortAsc, setSortAsc] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [ringbaUnavailable, setRingbaUnavailable] = useState(false);
  const intervalRef = useRef(null);

  const fetchNames = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/publisher-control/names`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPublisherNames(res.data.publishers || []);
    } catch (err) {
      console.error("Failed to fetch publisher names:", err);
    }
  }, []);

  useEffect(() => { fetchNames(); }, [fetchNames]);

  const fetchData = useCallback(async (silent = false) => {
    if (!dateFilter) return;
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("start_date", dateFilter.startDate);
      params.set("end_date", dateFilter.endDate);
      if (publisherFilter) params.set("publisher_name", publisherFilter);

      const res = await axios.get(`${API}/api/publisher-report/data?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData({
        publishers: res.data.publishers || [],
        summary: res.data.summary || {},
      });
      if (Array.isArray(res.data.publisher_names) && res.data.publisher_names.length) {
        setPublisherNames(res.data.publisher_names);
      }
      setRingbaUnavailable(!!res.data.ringba_unavailable);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Publisher report fetch error:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter, publisherFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!dateFilter) return;
    intervalRef.current = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [dateFilter, publisherFilter, fetchData]);

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/publisher-control/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await Promise.all([fetchNames(), fetchData(true)]);
      toast.success("Publisher data synced");
    } catch (err) {
      console.error("Publisher report sync error:", err);
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const sorted = [...(data?.publishers || [])].sort((a, b) => {
    const av = a[sortCol] ?? 0;
    const bv = b[sortCol] ?? 0;
    if (typeof av === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortAsc ? av - bv : bv - av;
  });

  const summary = data?.summary || {};

  const exportCSV = () => {
    if (!sorted.length) { toast.error("No data to export"); return; }
    const hdr = ["Publisher Name", "Total Calls", "Valid Calls", "Problem Calls", "Fake Calls", "Fake %", "Status"];
    const rows = sorted.map((p) => [
      `"${p.publisher_name}"`, p.total_calls, p.valid_calls, p.problem_calls, p.fake_calls, p.fake_pct, p.status,
    ]);
    const csv = [hdr.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `publisher_report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success(`Exported ${sorted.length} publishers`);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 text-slate-600" />;
    return sortAsc ? <ChevronUp className="h-3 w-3 text-blue-400" /> : <ChevronDown className="h-3 w-3 text-blue-400" />;
  };

  return (
    <div className="space-y-6" data-testid="publisher-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Publisher Report</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Performance summary and risk classification
            {lastUpdated && <span className="ml-1 sm:ml-2 text-slate-600" data-testid="last-updated-report">&middot; Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <select
              value={publisherFilter}
              onChange={(e) => setPublisherFilter(e.target.value)}
              className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer max-w-[180px]"
              data-testid="report-publisher-filter"
            >
              <option value="" className="bg-slate-900">All Publishers</option>
              {publisherNames.map((n) => (
                <option key={n} value={n} className="bg-slate-900">{n}</option>
              ))}
            </select>
          </div>
          <SyncDataButton onSync={handleSync} syncing={syncing} lastSynced={lastUpdated} ringbaUnavailable={ringbaUnavailable} testId="sync-data-btn" />
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800/60 transition"
            data-testid="export-csv-btn"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Date Filter - same as Call Analysis */}
      <DateFilter onFilterChange={setDateFilter} defaultPreset="today" />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SummaryCard title="Total Publishers" value={summary.total_publishers ?? 0} icon={Users} color="bg-blue-500/10 text-blue-400" />
            <SummaryCard title="Active Publishers" value={summary.active_publishers ?? 0} icon={ShieldCheck} color="bg-emerald-500/10 text-emerald-400" />
            <SummaryCard title="Inactive Publishers" value={summary.inactive_publishers ?? 0} icon={UserX} color="bg-slate-500/10 text-slate-400" />
            <SummaryCard title="High Risk" value={summary.high_risk ?? 0} icon={ShieldAlert} color="bg-red-500/10 text-red-400" />
          </div>

          {/* Table */}
          <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="publisher-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800/50">
                    {[
                      { key: "publisher_name", label: "Publisher Name" },
                      { key: "total_calls", label: "Total Calls" },
                      { key: "valid_calls", label: "Valid Calls" },
                      { key: "problem_calls", label: "Problem Calls" },
                      { key: "fake_pct", label: "Fake Call %" },
                      { key: "status", label: "Status" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-300 select-none"
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="flex items-center gap-1.5">{col.label} <SortIcon col={col.key} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No publisher data for the selected range</td></tr>
                  ) : (
                    sorted.map((pub, i) => {
                      const cfg = STATUS_CONFIG[pub.status] || STATUS_CONFIG.Trusted;
                      return (
                        <tr
                          key={`${pub.publisher_name}-${i}`}
                          className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors"
                          data-testid={`publisher-row-${i}`}
                        >
                          <td className="px-4 py-3 font-medium text-slate-200 whitespace-nowrap">
                            <span className="group inline-flex items-center gap-1">
                              {pub.publisher_name}
                              <CopyButton text={pub.publisher_name} label="publisher name" testId={`copy-name-${i}`} />
                              {pub.publisher_id && <CopyButton text={pub.publisher_id} label={`publisher ID ${pub.publisher_id}`} testId={`copy-id-${i}`} />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300 tabular-nums">{pub.total_calls}</td>
                          <td className="px-4 py-3 text-emerald-400 tabular-nums">{pub.valid_calls}</td>
                          <td className="px-4 py-3 text-amber-400 tabular-nums">{pub.problem_calls}</td>
                          <td className="px-4 py-3 tabular-nums">
                            <span className={cn("font-semibold", pub.fake_pct >= 50 ? "text-red-400" : pub.fake_pct >= 20 ? "text-amber-400" : "text-emerald-400")}>
                              {pub.fake_pct}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border", cfg.bg, cfg.text, cfg.border)}>
                              <span className={cn("w-1.5 h-1.5 rounded-full", cfg.dot)} />
                              {pub.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ title, value, icon: Icon, color }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 flex items-center gap-4" data-testid={`summary-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={cn("p-3 rounded-xl", color)}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</span>
        <p className="text-2xl font-bold text-slate-100 mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
      </div>
    </div>
  );
}
